import axios from 'axios';
import { Source, CreateSourceDto, Stats, AuthResponse, LoginDto, RegisterDto, User, Invitation, CreateInvitationDto, TagDetailDto, Author, CreateAuthorDto, UpdateAuthorDto } from '../types';

// API_BASE_URL is configured via environment variables
// .env.development: http://localhost:5222/api
// .env.production: https://api.useful.wisebox.hu/api
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5222/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const sourcesApi = {
  getSources: async (params?: {
    search?: string;
    tags?: string;
    favorites?: boolean;
    minRating?: number;
    platform?: string;
    author?: string;
    dateFrom?: string;
    dateTo?: string;
    mySourcesFilter?: string;
    showOthersPublic?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const response = await api.get<Source[]>('/sources', { params });
    // Headers might be lowercase in axios response
    const totalCountHeader = response.headers['x-total-count'] || response.headers['X-Total-Count'];
    const pageHeader = response.headers['x-page'] || response.headers['X-Page'];
    const pageSizeHeader = response.headers['x-page-size'] || response.headers['X-Page-Size'];

    return {
      data: response.data,
      totalCount: parseInt(totalCountHeader || '0'),
      page: parseInt(pageHeader || '1'),
      pageSize: parseInt(pageSizeHeader || '20'),
    };
  },

  getPublicSources: async (): Promise<Source[]> => {
    const response = await api.get<Source[]>('/sources/public/all');
    return response.data;
  },

  getSource: async (id: number): Promise<Source> => {
    const response = await api.get<Source>(`/sources/${id}`);
    return response.data;
  },

  createSource: async (source: CreateSourceDto): Promise<Source> => {
    const response = await api.post<Source>('/sources', source);
    return response.data;
  },

  updateSource: async (id: number, source: CreateSourceDto): Promise<void> => {
    await api.put(`/sources/${id}`, source);
  },

  deleteSource: async (id: number): Promise<void> => {
    await api.delete(`/sources/${id}`);
  },

  toggleFavorite: async (id: number): Promise<Source> => {
    const response = await api.post<Source>(`/sources/${id}/toggle-favorite`);
    return response.data;
  },

  togglePrivacy: async (id: number): Promise<Source> => {
    const response = await api.post<Source>(`/sources/${id}/toggle-privacy`);
    return response.data;
  },

  getStats: async (): Promise<Stats> => {
    const response = await api.get<Stats>('/sources/stats');
    return response.data;
  },
};

export const authApi = {
  login: async (credentials: LoginDto): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    return response.data;
  },

  register: async (data: RegisterDto): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    return response.data;
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<AuthResponse>('/auth/me');
    return {
      userId: response.data.userId,
      email: response.data.email,
      firstName: response.data.firstName,
      lastName: response.data.lastName,
      role: response.data.role,
    };
  },

  changePassword: async (currentPassword: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>('/auth/change-password', {
      currentPassword,
      newPassword,
      confirmPassword,
    });
    return response.data;
  },
};

export const invitationsApi = {
  getInvitations: async (): Promise<Invitation[]> => {
    const response = await api.get<Invitation[]>('/invitations');
    return response.data;
  },

  createInvitation: async (data: CreateInvitationDto): Promise<Invitation> => {
    const response = await api.post<Invitation>('/invitations', data);
    return response.data;
  },

  deleteInvitation: async (id: number): Promise<void> => {
    await api.delete(`/invitations/${id}`);
  },
};

export const usersApi = {
  getAllUsers: async (): Promise<any[]> => {
    const response = await api.get<any[]>('/users');
    return response.data;
  },

  getUser: async (id: string): Promise<any> => {
    const response = await api.get<any>(`/users/${id}`);
    return response.data;
  },

  updateUserRole: async (id: string, role: string): Promise<void> => {
    await api.put(`/users/${id}/role`, { role });
  },

  toggleUserActive: async (id: string): Promise<void> => {
    await api.put(`/users/${id}/toggle-active`);
  },

  // User Profile endpoints
  getProfile: async (): Promise<any> => {
    const response = await api.get<any>('/users/profile');
    return response.data;
  },

  updateProfile: async (firstName: string, lastName: string): Promise<void> => {
    await api.put('/users/profile', { firstName, lastName });
  },

  // User Preferences endpoints
  getPreferences: async (): Promise<any> => {
    const response = await api.get<any>('/users/preferences');
    return response.data;
  },

  updatePreferences: async (preferences: any): Promise<void> => {
    await api.put('/users/preferences', preferences);
  },
};

export const tagsApi = {
  getAllTags: async (params?: {
    page?: number;
    pageSize?: number;
    filterName?: string;
    filterId?: number;
    filterMinUsage?: string;
    filterMaxUsage?: string;
    filterCreatedBy?: string;
    filterDateFrom?: string;
    filterDateTo?: string;
  }): Promise<{ data: TagDetailDto[]; totalCount: number; totalPages: number; currentPage: number; pageSize: number }> => {
    const response = await api.get<any>('/tags', { params });
    return response.data;
  },

  getTag: async (id: number): Promise<TagDetailDto> => {
    const response = await api.get<TagDetailDto>(`/tags/${id}`);
    return response.data;
  },

  createTag: async (tag: { name: string; color?: string }): Promise<TagDetailDto> => {
    const response = await api.post<TagDetailDto>('/tags', tag);
    return response.data;
  },

  updateTag: async (id: number, tag: { name: string; color?: string }): Promise<void> => {
    await api.put(`/tags/${id}`, tag);
  },

  deleteTag: async (id: number): Promise<void> => {
    await api.delete(`/tags/${id}`);
  },

  getTagStatistics: async (): Promise<any> => {
    const response = await api.get<any>('/tags/statistics/overview');
    return response.data;
  },
};

export const authorsApi = {
  getAuthors: async (params?: {
    search?: string;
    includePrivate?: boolean;
    page?: number;
    pageSize?: number;
  }): Promise<{ data: Author[]; totalCount?: number }> => {
    const response = await api.get<Author[]>('/authors', { params });
    return {
      data: response.data,
      totalCount: response.data.length,
    };
  },

  getAuthor: async (id: number): Promise<Author> => {
    const response = await api.get<Author>(`/authors/${id}`);
    return response.data;
  },

  createAuthor: async (author: CreateAuthorDto): Promise<Author> => {
    const response = await api.post<Author>('/authors', author);
    return response.data;
  },

  updateAuthor: async (id: number, author: UpdateAuthorDto): Promise<void> => {
    await api.put(`/authors/${id}`, author);
  },

  deleteAuthor: async (id: number): Promise<void> => {
    await api.delete(`/authors/${id}`);
  },

  searchAuthors: async (query: string): Promise<Author[]> => {
    const response = await api.get<Author[]>(`/authors/search/${query}`);
    return response.data;
  },
};